// Data types
// Dependencies
import React, { RefObject } from 'react';
// Components
import { connect } from 'react-redux';

import { HeatmapLayer, ScreenGridLayer } from '@deck.gl/aggregation-layers';
import { Deck as DeckGl, FlyToInterpolator } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import BaseMap from 'components/baseClass/MapBase';
import {
    COLOR_RANGES,
    HIGHLIGHTED_OUTLINE_INDEX_COLOR,
    HIGHLIGHTED_SWEPTPATH_COLOR,
    OUTLINE_INTERVALS,
    SWEPTPATH_COLOR,
} from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
// Import Nebula mode
import {
    setErrorHandlerAction,
    SetErrorHandlerAction,
    setSystemLoadingAction,
    SetSystemLoadingAction,
    ShowMessageAction,
    showMessageAction,
} from 'store/actions/errorsActions';
import { setMapAction, SetMapAction, setMapControlsAction, SetMapControlsAction } from 'store/actions/mapActions';
import {
    BackgroundType,
    CoordinateArray,
    DeckGlInstance,
    DeckGLLayer,
    LayerType,
    Layout,
    MapPositionType,
    MapProps,
    OpenModal,
    PilotagePlanSettingType,
    PlanType,
    PortType,
    PositionType,
    RGBAColor,
    RootState,
    SwitchType,
    ToolTipType,
    TransitsDataType,
    TransitType,
    VesselCategoryType,
} from 'types';
import { MainControlsInterface, MenuControlsInterface } from 'types/functions';
import { GeoJSONFeature, GeoJSONFeatureCollection } from 'types/geojson';
import { hexToRgb } from 'utils/colors';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { vesselOutline } from 'utils/drawing';
import { checkDifferent, checkLayoutDifferent, clone, getId } from 'utils/formHandlers';
import geometry from 'utils/geometry';
import { filterBySection, getLayerProvider, getPortCoordinate, updateFeatureProperties } from 'utils/layers';
import { twclsx } from 'utils/twclsx';

import Image from 'components/atoms/Image';
import { translate } from 'utils/translate';
import Attribution from './Attribution';
import ChangeLayer from './ChangeLayer';
import Controls from './Controls';
import MeasurePosition from './MeasurePosition';
import MousePosition from './MousePosition';
import ToolTip from './ToolTip';

interface ChartComponentProps {
    layout: Layout;
    active: boolean;

    port: PortType;

    outlineInterval: number;
    switchs: SwitchType;
    layers: LayerType;
    setMapAction: SetMapAction;
    openModelFunction: OpenModal;
    setMapControlsAction: SetMapControlsAction;
    vesselCategories: VesselCategoryType[];

    //unused props
    setErrorHandlerAction: SetErrorHandlerAction;

    //unused props
    setSystemLoadingAction: SetSystemLoadingAction;

    //unused props
    showMessageAction: ShowMessageAction;

    pilotagePlans: PlanType[];
    pilotagePlanSettings: PilotagePlanSettingType;
    dateTimeFormat: DateTimeFormat;
    mainControls: MainControlsInterface;

    //unused props
    menuControls: MenuControlsInterface;
}

interface ChartComponentState {
    layout: Layout;
    active: boolean;

    uuid: string;
    zoom: number;

    //unused state
    mode: string;

    coordinate: CoordinateArray | null;
    hoverMeasureData: any | null;
    backgrounds: BackgroundType[];
    mapProps?: MapProps;
    switchs: SwitchType;
    map?: DeckGlInstance;
    tooltips: ToolTipType[];

    //unused state
    areaFeatures: GeoJSONFeature[];

    drawingFeatures: GeoJSONFeatureCollection;

    //unused state
    markingFeatures: GeoJSONFeature[];
    clickMatrix: any[];
    sweptPathFeatures: (GeoJSONFeature | null)[];
    trackFeatures: (GeoJSONFeature | null)[];
    outlineFeatures: (GeoJSONFeature | null)[];
    pilotagePlans: PlanType[];
    pilotagePlanSettings: PilotagePlanSettingType;
    refreshFeatures: string;
    layers: LayerType;

    //unused state
    drawingMode: string;
}

class Chart extends BaseMap<ChartComponentProps, ChartComponentState> {
    private canvasRef: RefObject<HTMLCanvasElement>;

    constructor(props: ChartComponentProps) {
        super(props);
        this.state = {
            layout: clone(props.layout),
            drawingMode: 'pointer',
            hoverMeasureData: null,
            mode: 'search',
            uuid: '',
            active: true,
            zoom: 10,
            drawingFeatures: {
                type: 'FeatureCollection',
                features: [],
            },
            clickMatrix: [],
            coordinate: null,
            backgrounds: [],
            tooltips: [],
            areaFeatures: [],
            sweptPathFeatures: [],
            markingFeatures: [],
            outlineFeatures: [],
            trackFeatures: [],
            switchs: clone(props.switchs),
            pilotagePlans: [],
            pilotagePlanSettings: {},
            refreshFeatures: getId(),
            layers: clone(props.layers),
        };
        // Initialize the ref in the constructor
        this.canvasRef = React.createRef();
        //@ts-ignore
        this.geometry = new geometry(props.port?.projection, 'EPSG:4326');
    }

    static getDerivedStateFromProps(props: ChartComponentProps, state: ChartComponentState) {
        // Stop COMM update if chart is not active
        if (!props.layout.sections?.chart.state) {
            return {
                active: props.active,
                layout: clone(props.layout),
            };
        }

        const changed =
            props.active !== state.active ||
            checkDifferent(props.switchs, state.switchs, true) ||
            checkDifferent(props.layers, state.layers) ||
            checkLayoutDifferent(props.layout, state.layout, ['sections.chart']) ||
            checkDifferent(props.pilotagePlanSettings, state.pilotagePlanSettings, true) ||
            checkDifferent(props.pilotagePlans, state.pilotagePlans, true);

        if (!changed) {
            return null;
        }

        return {
            active: props.active,
            switchs: clone(props.switchs),
            layers: clone(props.layers),
            layout: clone(props.layout),
            pilotagePlans: props.pilotagePlans,
            pilotagePlanSettings: props.pilotagePlanSettings,
        };
    }
    componentDidMount() {
        const { latitude, longitude, zoom } = getPortCoordinate(this.props.port);
        this.setState(
            { uuid: getId(), zoom, coordinate: [latitude, longitude], backgrounds: this.getPortBackgrounds('chart') },
            this.createMap
        );

        const canvas = this.canvasRef.current;

        if (canvas) {
            canvas.addEventListener('mousedown', this.handleMouseDown);
            document.addEventListener('mouseup', this.handleMouseUp);
        }
    }

    componentWillUnmount() {
        const canvas = this.canvasRef.current;

        if (canvas) {
            canvas.removeEventListener('mousedown', this.handleMouseDown);
            document.removeEventListener('mouseup', this.handleMouseUp);
        }
    }

    handleMouseDown = () => {
        const canvas = this.canvasRef.current;
        if (canvas) {
            canvas.style.setProperty('cursor', 'grabbing', 'important');
        }
    };

    handleMouseUp = () => {
        const canvas = this.canvasRef.current;
        if (canvas) {
            canvas.style.setProperty('cursor', 'default', 'important');
        }
    };

    async componentDidUpdate(
        props: Readonly<ChartComponentProps>,
        state: Readonly<ChartComponentState>
    ): Promise<void> {
        // Check if state.switches has changed

        if (
            checkDifferent(
                filterBySection(state.switchs, 'analysisAreas'),
                filterBySection(this.state.switchs, 'analysisAreas')
            ) &&
            this.state.map
        ) {
            this.updatePortAreas();
        }

        if (
            Object.values(this.state.layout.sections.chart.tools).every((tool) => tool.selected === false) &&
            this.state.drawingMode !== 'pointer'
        ) {
            this.setDrawingMode('pointer');
        }

        // if (
        //     (checkDifferent(state.switchs.heatmap, this.state.switchs.heatmap, true) ||
        //         checkDifferent(state.layers.heatmap, this.state.layers.heatmap, true)) &&
        //     this.state.map
        // ) {
        //     this.updateHeatmap();
        // }

        // if (
        //     (checkDifferent(state.switchs.screenGrid, this.state.switchs.screenGrid, true) ||
        //         checkDifferent(state.layers.screenGrid, this.state.layers.screenGrid, true)) &&
        //     this.state.map
        // ) {
        //     this.updateScreenGrid();
        // }

        if (checkDifferent(state.drawingFeatures, this.state.drawingFeatures, true) && this.state.map) {
            await this.createMeasureTooltip();
            await this.createLabelTooltip();
            await this.createCoordinateTooltip();
            this.createEditlayer();
        }
        // Check if pilotagePlanSettings or pilotagePlanSettings has changed
        if (
            checkDifferent(state.pilotagePlanSettings, this.state.pilotagePlanSettings, true) ||
            (checkDifferent(state.pilotagePlans, this.state.pilotagePlans, true) && this.state.map)
        ) {
            this.updatePilotagePlans(state.pilotagePlans);
        }
        //TODO set update for each switch only
        if (checkDifferent(state.refreshFeatures, this.state.refreshFeatures) && this.state.map) {
            // Execute the updates sequentially
            window?.log('INFO : map feature update requested');
            this.updateSweptPaths()
                .then(() => this.updateTransitTracks())
                .then(() => this.updateVesselsOulines(false))
                .catch((error) => console.error('Error updating features:', error));
        }
    }

    /**
     * Creates the map with background layers and a GeoJson layer.
     *
     * @returns {Promise<void>} A promise indicating the completion of map creation.
     */
    createMap = async () => {
        const savedData = this.props.mainControls.getLoadSavedData(false);
        const layers = [
            await getLayerProvider(
                this.state.coordinate,
                this.state.backgrounds.find((x) => {
                    return x.status === 'active' && x.type === 'basemap';
                }),
                0
            ),
            await getLayerProvider(
                this.state.coordinate,
                this.state.backgrounds.find((x) => {
                    return x.status === 'active' && x.type === 'overlay';
                }),
                1
            ),
        ].filter((item) => !!item);

        const mapProps = {
            canvas: this.state.uuid,
            viewState: {
                latitude: savedData ? savedData.data.map.latitude : this.state.coordinate?.[0],
                longitude: savedData ? savedData.data.map.longitude : this.state.coordinate?.[1],
                zoom: savedData ? savedData.data.map.zoom : this.state.zoom - 1,
                bearing: 0,
                pitch: 0,
                altitude: 1,
                maxZoom: 18,
                minZoom: 10,
                maxPitch: 60,
                minPitch: 0,
                normalize: true,
                position: [0, 0, 0],
                width: this.canvasRef?.current?.clientWidth,
                height: this.canvasRef?.current?.clientHeight,
            },
            initialViewState: {
                latitude: savedData ? savedData.data.map.latitude : this.state.coordinate?.[0],
                longitude: savedData ? savedData.data.map.longitude : this.state.coordinate?.[1],
                zoom: savedData ? savedData.data.map.zoom : this.state.zoom - 1,
                bearing: 0,
                pitch: 0,
                altitude: 1,
                maxZoom: 18,
                minZoom: 10,
                maxPitch: 60,
                minPitch: 0,
                normalize: true,
                position: [0, 0, 0],
                width: this.canvasRef?.current?.clientWidth,
                height: this.canvasRef?.current?.clientHeight,
            },
            tooltip: false,
            controller: {
                doubleClickZoom: true,
                keyboard: false,
                dragPan: true,
            },
            getCursor: ({ isDragging }) => {
                return isDragging ? 'grabbing !important' : 'default';
            },
            onHover: this.handleOnHover,
            onDragEnd: this.handleOnDragEnd,
            onDragStart: this.handleOnDragStart,
            onDrag: this.handleOnDrag,
            onClick: this.handleOnClick,
            onViewStateChange: this.handleViewStateChange,
            onAfterRender: this.handleAfterRender,
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onEvent: ({ type, srcEvent }) => {},
            layers: layers,
        };

        window?.log('EVENT : Chart map created', 'color: orange');
        const map = new DeckGl(mapProps);
        //@ts-ignore
        this.setState({ map, mapProps: { ...mapProps, canvas: map.getCanvas() } }, async () => {
            this.props.setMapAction({ chart: map });
            this.props.setMapControlsAction({
                chart: {
                    hideTooltips: this.hideTooltips,
                    getTooltips: this.getTooltips,
                    setTooltips: this.setTooltips,
                    clearTooltips: this.clearTooltips,
                    getDrawingCount: this.getDrawingCount,
                    clearMap: this.clearMap,
                    hideTooltipById: this.hideTooltipById,
                    flagTooltipId: this.flagTooltipId,
                    removeDrawingById: this.removeDrawingById,
                    removeDrawingToolTipById: this.removeDrawingToolTipById,
                    updateTransits: this.updateTransits,
                    refreshFeatures: this.refreshFeatures,
                    portAreaFeatureVisiblity: this.portAreaFeatureVisiblity,
                    createMarkingLayer: this.createMarkingLayer,
                    setDrawingMode: this.setDrawingMode,
                    getDrawingMode: this.getDrawingMode,
                    getDrawingFeatures: this.getDrawingFeatures,
                    zoomIn: this.zoomIn,
                    zoomOut: this.zoomOut,
                    getViewPort: this.getViewPort,
                    setViewPort: this.setViewPort,
                    getClickMatrix: this.getClickMatrix,
                    setClickMatrix: this.setClickMatrix,
                    updateMatrixIndex: this.updateMatrixIndex,
                },
            });
            this.createDrawingLayer();
            this.createPortAreas(this.state.pilotagePlans.length >= 1);
        });
    };

    getViewPort = () => {
        return {
            latitude: this.state.mapProps?.viewState?.latitude,
            longitude: this.state.mapProps?.viewState?.longitude,
            zoom: this.state.mapProps?.viewState?.zoom,
            backgrounds: this.state.backgrounds,
        };
    };

    setViewPort = (
        latitude: number,
        longitude: number,
        zoom: number,
        background: BackgroundType,
        overlay: BackgroundType | undefined
    ) => {
        this.setState(
            {
                mapProps: {
                    ...this.state.mapProps,
                    //@ts-ignore
                    viewState: {
                        ...this.state.mapProps?.viewState,
                        longitude,
                        latitude,
                        zoom,
                        transitionDuration: 50, // Smooth transition duration
                        transitionInterpolator: new FlyToInterpolator(), // Smooth fly-to effect}
                    },
                },
            },
            () => {
                this.changeBackground(background, 'chart', false);
                if (overlay) {
                    this.changeOverlay(overlay, 'chart', false);
                }
                this.state.map?.setProps({ viewState: this.state.mapProps?.viewState });
            }
        );
    };

    updateHeatmap = async () => {
        window?.log('INFO : Updating heatmap');

        // eslint-disable-next-line prefer-const
        let { transits, transitsData } = this.props.mainControls.getTransit(0);
        transits = transits.filter((transit: TransitType) => transit.options.display);

        let data: MapPositionType[][] = [];
        if (this.state.switchs.heatmap.value) {
            data = transits.map((transit: TransitType) => {
                return transitsData[transit.uuid][transit.best].map(
                    (position: PositionType): MapPositionType => ({
                        drift_angle_deg: position.drift_angle_deg,
                        crot_deg_min: position.crot_deg_min,
                        cog_deg: position.cog_deg,
                        sog_mps: position.sog_mps,
                        rot_deg_min: position.rot_deg_min,
                        coordinates: this.geometry.transformer([position.east_utm, position.north_utm]) as [
                            number,
                            number
                        ],
                    })
                );
            });
        }

        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        layers = this.replaceLayers(
            layers,
            [
                //@ts-ignore // this function is not ready yet
                new HeatmapLayer<MapPositionType>({
                    id: this.props.layers.heatmap.uuid,
                    data: data.flat() as unknown as MapPositionType[], // now MapPositionType[]
                    getPosition: (d: MapPositionType) => d.coordinates as [number, number],
                    getWeight: (d: MapPositionType) => {
                        if (this.props.layers.heatmap.options?.groupBy === 'coordinates') {
                            return 10;
                        }
                        return d[this.props.layers.heatmap.options?.groupBy || 'sog_mps'];
                    },
                    aggregation: this.props.layers.heatmap.options?.aggregation as 'SUM' | 'MEAN' | undefined,
                    colorDomain: [1, transits.length],
                    colorRange: COLOR_RANGES[this.props.layers.screenGrid.options?.colorRange || 'warm'],
                    radiusPixels: this.props.layers.heatmap.options?.radiusPixels,
                    intensity: this.props.layers.heatmap.options?.intensity,
                    threshold: this.props.layers.heatmap.options?.threshold,
                    //@ts-ignore
                    index: this.props.layers.heatmap.drawIndex,
                }),
            ],
            true
        );

        await this.renderMap({ layers });
    };

    updateScreenGrid = async () => {
        window?.log('INFO : Updating screenGrid');

        //@ts-ignore
        // zero will never return undefind will return empty array

        const { transits, transitsData } = this.props.mainControls.getTransit(0);

        const displayTransits = transits.filter((transit: TransitType) => {
            return transit.options.display;
        });

        let data: TransitType[] = [];
        if (this.state.switchs.screenGrid.value) {
            data = displayTransits.map((transit: TransitType) => {
                return transitsData[transit.uuid][transit.best].map((position: PositionType): MapPositionType => {
                    return {
                        drift_angle_deg: position.drift_angle_deg,
                        crot_deg_min: position.crot_deg_min,
                        cog_deg: position.cog_deg,
                        sog_mps: position.sog_mps,
                        rot_deg_min: position.rot_deg_min,
                        coordinates: this.geometry.transformer([position.east_utm, position.north_utm]),
                    };
                });
            });
        }

        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        layers = this.replaceLayers(
            layers,
            [
                new ScreenGridLayer({
                    id: this.props.layers.screenGrid.uuid,
                    data: data.flat() as unknown as MapPositionType[],
                    cellSizePixels: this.props.layers.screenGrid.options?.cellSizePixels,
                    colorRange: COLOR_RANGES[this.props.layers.screenGrid.options?.colorRange || 'warm'],
                    getPosition: (d: MapPositionType) => d.coordinates as [number, number],
                    getWeight: (d: MapPositionType) => {
                        if (this.props.layers.screenGrid.options?.groupBy === 'coordinates') {
                            return 10;
                        }

                        return d[this.props.layers.screenGrid.options?.groupBy || 'sog_mps'];
                    },
                    opacity: this.props.layers.screenGrid.options?.opacity,
                }) as unknown as DeckGLLayer,
            ],
            true
        );
        await this.renderMap({ layers: layers });
    };

    updateTransits = (transits: TransitType[], transitsData: TransitsDataType, options: string[]) => {
        window?.log('INFO : Map feature replace requested');

        // Helper functions for color and display logic
        const getFillColor = (
            id: number | string,
            defaultColorKey: string,
            highlightColorKey: string,
            color?: string
        ) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[defaultColorKey]?.default;
            const highlightColor = this.props.port.meta_data.defaultColours?.[highlightColorKey]?.default;

            if (
                this.props.mainControls.getOption(id, ['highlight']) ||
                this.props.mainControls.getOption(id, ['inspect'])
            ) {
                return { colour: highlightColor, opacity: 0.2 };
            }
            if (this.state.switchs['vesselOutColor'].value && color) {
                return { colour: color, opacity: 0.2 };
            }

            return { colour: defaultColor, opacity: 0.2 };
        };

        const getStrokeColor = (
            id: number | string,
            defaultColorKey: string,
            highlightColorKey: string,
            color?: string
        ) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[defaultColorKey]?.default;
            const highlightColor = this.props.port.meta_data.defaultColours?.[highlightColorKey]?.default;

            if (this.props.mainControls.getOption(id, ['inspect'])) {
                return { colour: highlightColor, opacity: 1, width: 2 };
            }

            if (this.state.switchs['vesselOutColor'].value && color) {
                return { colour: color, opacity: 1, width: 2 };
            }

            return { colour: defaultColor, opacity: 1, width: 2 };
        };

        const getDisplayStatus = (id: number | string, optionKey: string, switchKey: string) => {
            // if default of the switch is OFF then will show item if the switch is moved to ON
            // or if user select the transit switch ON
            if (optionKey === 'outline' && this.props.outlineInterval === 0) {
                return false;
            }

            //REMOVE 2.1
            return this.props.mainControls.getOption(id, ['select', 'display', optionKey]);

            //BACK 2.1
            //return this.props.mainControls.getOption(id, ['display', optionKey]) || this.state.switchs[switchKey].value;
        };
        const updateState = {};
        // Generate new swept path features
        if (options.length === 0 || options.includes('drawSweptPath') || options.includes('display')) {
            updateState['sweptPathFeatures'] = transits
                .filter((transit) => {
                    //REMOVE 2.1
                    return transit.options.select && transit.options.display && transit.options.drawSweptPath;
                    //BACK 2.1
                    //return transit.options.display && transit.options.drawSweptPath && this.state.switchs.sweptPath.value;
                })
                .sort((a, b) => {
                    return (
                        //@ts-ignore
                        (this.props.mainControls.getOption(a.id, ['highlight']) ? 1 : 0) -
                        //@ts-ignore
                        (this.props.mainControls.getOption(b.id, ['highlight']) ? 1 : 0)
                    );
                })
                .map((transit) => {
                    const multipolygon: GeoJSONFeature = {
                        type: 'Feature',
                        geometry: {
                            type: 'MultiPolygon',
                            coordinates: [],
                        },
                    };

                    transitsData[transit.uuid]?.sweptPath?.forEach((sweptPath) => {
                        const geoJSON = this.geometry.transformerGEOJSON(this.geometry.parseWKT(sweptPath.geom));
                        if (geoJSON?.geometry?.type === 'Polygon') {
                            //@ts-ignore
                            multipolygon.geometry.coordinates.push(geoJSON.geometry.coordinates);
                        } else if (geoJSON?.geometry?.type === 'MultiPolygon') {
                            //@ts-ignore
                            multipolygon.geometry.coordinates = geoJSON.geometry.coordinates.map((polygon: any) =>
                                polygon.map((ring: any) => ring.map((coord: number[]) => coord.slice(0, 2)))
                            );
                        }
                    });

                    const display = getDisplayStatus(transit.id, 'drawSweptPath', 'sweptPath');
                    const highlightd = this.props.mainControls.getOption(transit.id, ['highlight']);
                    return updateFeatureProperties(
                        //@ts-ignore
                        transitsData[transit.uuid].sweptPathId,
                        multipolygon,
                        {
                            index: 0,
                            drawIndex: this.props.layers.sweptPath.drawIndex,
                            clickIndex: this.props.layers.sweptPath.clickIndex,
                            fillColorUpdate: () => {
                                return this.props.mainControls.getOption(transit.id, ['highlight'])
                                    ? HIGHLIGHTED_SWEPTPATH_COLOR
                                    : SWEPTPATH_COLOR;
                            },
                            strokeColorUpdate: () => {
                                return this.props.mainControls.getOption(transit.id, ['highlight'])
                                    ? { ...HIGHLIGHTED_SWEPTPATH_COLOR, opacity: 1 }
                                    : { ...SWEPTPATH_COLOR, opacity: 1 };
                            },
                            fill: highlightd ? HIGHLIGHTED_SWEPTPATH_COLOR : SWEPTPATH_COLOR,
                            stroke: highlightd
                                ? { ...HIGHLIGHTED_SWEPTPATH_COLOR, opacity: 1 }
                                : { ...SWEPTPATH_COLOR, opacity: 1 },
                            name: transit.vessel.name,
                            display,
                            visible: display,
                            displayUpdate: () => getDisplayStatus(transit.id, 'drawSweptPath', 'sweptPath'),
                            layer: this.props.layers.sweptPath.name,
                            layerGroup: this.props.layers.sweptPath.group,
                            parent: transit.id,
                            description: null,
                        },
                        this.props.layers.sweptPath.name
                    );
                });
        }

        if (options.length === 0 || options.includes('drawTrack') || options.includes('display')) {
            // Generate new track features
            updateState['trackFeatures'] = transits
                .filter((transit: TransitType) => {
                    //REMOVE 2.1
                    return transit.options.display && transit.options.select && transit.options.drawTrack;
                    //BACK 2.1
                    //return transit.options.display && (transit.options.drawTrack || this.state.switchs.vesselTracks.value);
                })
                .filter((transit: TransitType) => {
                    if (transitsData[transit.uuid][transit.best].length === 0) {
                        window?.log(`ERROR : Transit ${transit.id} has no best data`, 'color: red');
                        return false;
                    }
                    return true;
                })
                .map((transit) => {
                    const lineString: GeoJSONFeature = {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: transitsData[transit.uuid][transit.best].map((x) => [x.east_utm, x.north_utm]),
                        },
                    };

                    const display = getDisplayStatus(transit.id, 'drawTrack', 'vesselTracks');
                    return updateFeatureProperties(
                        transitsData[transit.uuid][`${transit.best}Id`],
                        this.geometry.transformerGEOJSON(lineString),
                        {
                            index: 0,
                            drawIndex: this.props.layers.transitTrack.drawIndex,
                            clickIndex: this.props.layers.transitTrack.clickIndex,
                            fillColorUpdate: () =>
                                getFillColor(transit.id, `${transit.best}Track`, 'selectedTransitColor'),
                            strokeColorUpdate: () =>
                                getStrokeColor(transit.id, `${transit.best}Track`, 'selectedTransitColor'),
                            fill: getFillColor(transit.id, `${transit.best}Track`, 'selectedTransitColor'),
                            stroke: getStrokeColor(transit.id, `${transit.best}Track`, 'selectedTransitColor'),
                            name: transit.vessel.name,
                            display,
                            visible: display,
                            displayUpdate: () => getDisplayStatus(transit.id, 'drawTrack', 'vesselTracks'),
                            layer: this.props.layers.transitTrack.name,
                            layerGroup: this.props.layers.transitTrack.group,
                            parent: transit.id,
                            description: null,
                        },
                        `${transit.best}Track`
                    );
                });
        }
        // Generate new outline features
        if (options.length === 0 || options.includes('outline') || options.includes('display')) {
            updateState['outlineFeatures'] =
                this.props.outlineInterval === 0
                    ? []
                    : transits
                          .filter((transit) => {
                              //REMOVE 2.1
                              return transit.options.select && transit.options.display && transit.options.outline;
                              //BACK 2.1
                              //return transit.options.display && (transit.options.outline || this.state.switchs.vesselOutline.value);
                          })
                          .map((transit) => {
                              // Extract features from the best data
                              const features = transitsData[transit.uuid][transit.best];

                              // Initialize an array to hold the reduced features
                              const reducedFeatures: typeof features = [];
                              if (features && features.length > 0) {
                                  // Sort features by time to ensure they are in chronological order
                                  const sortedFeatures = features.sort(
                                      (a, b) =>
                                          this.props.dateTimeFormat.parseDateTime(a.valid_time_utc).toTimestamp() -
                                          this.props.dateTimeFormat.parseDateTime(b.valid_time_utc).toTimestamp()
                                  );

                                  const matrix = this.getClickMatrix(transit.id);
                                  // Push the first feature to the reduced array
                                  reducedFeatures.push(sortedFeatures[0]);
                                  // fixed next index

                                  let lastTime = this.props.dateTimeFormat.parseDateTime(
                                      sortedFeatures[0].valid_time_utc
                                  );
                                  let skipFirst = false;
                                  // Iterate over the sorted features
                                  for (let i = 1; i < sortedFeatures.length; i++) {
                                      const currentTime = this.props.dateTimeFormat.parseDateTime(
                                          sortedFeatures[i].valid_time_utc
                                      );

                                      if (
                                          (skipFirst &&
                                              currentTime.getDiffInSeconds(lastTime) >= this.props.outlineInterval) ||
                                          (!skipFirst &&
                                              currentTime.getDiffInSeconds(lastTime) >=
                                                  matrix.skipInterval[
                                                      OUTLINE_INTERVALS.indexOf(this.props.outlineInterval)
                                                  ])
                                      ) {
                                          skipFirst = true;
                                          lastTime = currentTime.clone();
                                          reducedFeatures.push({ ...sortedFeatures[i], time: currentTime });
                                      }
                                  }
                              }

                              return reducedFeatures?.map((position, index, positions) => {
                                  const { outline, color } = vesselOutline(
                                      position,
                                      transit.vessel,
                                      //@ts-ignore
                                      transitsData[transit.uuid].offsets[position.vessel_offset_id],
                                      transit.vessel.vessel_category,
                                      this.props.vesselCategories,
                                      this.props.port.id,
                                      transit.best !== 'raw_ais'
                                  );
                                  const display = getDisplayStatus(transit.id, 'outline', 'vesselOutline');
                                  return updateFeatureProperties(
                                      `vesselOutLine-${transit.id}-${index}`,
                                      this.geometry.transformerGEOJSON({
                                          type: 'Feature',
                                          geometry: { type: 'Polygon', coordinates: [outline] },
                                      }),
                                      {
                                          dataIndex: {
                                              type: transit.best,
                                              i: index,
                                              index: position.id,
                                              time: position.valid_time_utc,
                                          },
                                          drawIndex: this.props.layers.vesselOutline.drawIndex,
                                          clickIndex: this.props.layers.vesselOutline.clickIndex,
                                          fillColorUpdate: () =>
                                              getFillColor(
                                                  transit.id,
                                                  'vesselOutlineMonoColour',
                                                  'vesselOutlineHighlightColour',
                                                  color
                                              ),
                                          strokeColorUpdate: (update = false) =>
                                              this.isSelectedIndex(transit.id, positions, index, update)
                                                  ? HIGHLIGHTED_OUTLINE_INDEX_COLOR
                                                  : getStrokeColor(
                                                        transit.id,
                                                        'vesselOutlineMonoColour',
                                                        'vesselOutlineHighlightColour',
                                                        color
                                                    ),
                                          fill: getFillColor(
                                              transit.id,
                                              'vesselOutlineMonoColour',
                                              'vesselOutlineHighlightColour',
                                              color
                                          ),
                                          stroke: this.isSelectedIndex(transit.id, positions, index)
                                              ? HIGHLIGHTED_OUTLINE_INDEX_COLOR
                                              : getStrokeColor(
                                                    transit.id,
                                                    'vesselOutlineMonoColour',
                                                    'vesselOutlineHighlightColour',
                                                    color
                                                ),
                                          name: transit.vessel.name,
                                          display,
                                          visible: display,
                                          displayUpdate: () => getDisplayStatus(transit.id, 'outline', 'vesselOutline'),
                                          layer: this.props.layers.vesselOutline.name,
                                          layerGroup: this.props.layers.vesselOutline.group,
                                          groupCount: reducedFeatures.length - 1,
                                          parent: transit.id,
                                          description: null,
                                      },
                                      this.props.layers.vesselOutline.name
                                  );
                              });
                          })

                          .flat();
        }
        // Update state with new features and re-render
        this.setState(updateState, this.renderTransits);
    };

    updateSweptPaths = (): Promise<void> => {
        return new Promise((resolve) => {
            window?.log('INFO : update saweptPath');
            const sweptPathFeatures: GeoJSONFeature[] = clone(this.state.sweptPathFeatures);

            this.setState(
                {
                    sweptPathFeatures: sweptPathFeatures
                        .sort((a, b) => {
                            return (
                                //@ts-ignore
                                (this.props.mainControls.getOption(a.properties.parent, ['highlight']) ? 1 : 0) -
                                //@ts-ignore
                                (this.props.mainControls.getOption(b.properties.parent, ['highlight']) ? 1 : 0)
                            );
                        })
                        .map((feature) => {
                            return {
                                type: feature.type,
                                geometry: feature.geometry,
                                properties: {
                                    ...feature.properties,
                                    stroke: feature.properties?.strokeColorUpdate(false),
                                    fill: feature.properties?.fillColorUpdate(),
                                    visible: feature.properties?.displayUpdate(),
                                },
                            };
                        }),
                },
                () => {
                    this.renderTransits();
                    resolve(); // Resolve the promise after `setState` completes
                }
            );
        });
    };

    updateTransitTracks = (): Promise<void> => {
        return new Promise((resolve) => {
            window?.log('INFO : update transit tracks');
            const trackFeatures: GeoJSONFeature[] = clone(this.state.trackFeatures);

            this.setState(
                {
                    trackFeatures: trackFeatures.map((feature) => {
                        return {
                            type: feature.type,
                            geometry: feature.geometry,
                            properties: {
                                ...feature.properties,
                                stroke: feature.properties?.strokeColorUpdate(false),
                                fill: feature.properties?.fillColorUpdate(),
                                visible: feature.properties?.displayUpdate(),
                            },
                        };
                    }),
                },
                () => {
                    this.renderTransits();
                    resolve(); // Resolve the promise after `setState` completes
                }
            );
        });
    };

    updateVesselsOulines = (update: boolean): Promise<void> => {
        return new Promise((resolve) => {
            window?.log('INFO : update vessels oulines');
            const outlineFeatures: GeoJSONFeature[] = clone(this.state.outlineFeatures);

            this.setState(
                {
                    outlineFeatures: outlineFeatures
                        .map((feature) => {
                            return {
                                type: feature.type,
                                geometry: feature.geometry,
                                properties: {
                                    ...feature.properties,
                                    stroke: feature.properties?.strokeColorUpdate(update),
                                    fill: feature.properties?.fillColorUpdate(),
                                    visible: feature.properties?.displayUpdate(),
                                },
                            };
                        })
                        .sort((a, b) => {
                            if (a.properties.stroke.colour === HIGHLIGHTED_OUTLINE_INDEX_COLOR.colour) {
                                return 1;
                            }
                            return -1;
                        }),
                },
                () => {
                    this.renderTransits();
                    resolve(); // Resolve the promise after `setState` completes
                }
            );
        });
    };

    //unused function
    afterClick = async (id: number, time: DateTimeFormat | null) => {
        if (id === 0 || id == null) {
            this.setState({
                clickMatrix: [],
            });
            if (this.state.layout.inspectTransit === 0 || this.state.layout.inspectTransit === '') {
                return;
            }
        }
        console.time('step C 1: initial inspectTransit');
        await this.props.mainControls.inspectTransit(id, this.props.dateTimeFormat.parseDateTime(time));
        console.timeEnd('step C 1: initial inspectTransit');
    };

    renderTransits = async () => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        const features = [...this.state.outlineFeatures, ...this.state.sweptPathFeatures, ...this.state.trackFeatures];

        layers = this.replaceLayers(
            layers,
            Object.values(this.props.layers)
                .filter((layer) => layer.group === 'features' || layer.group === 'vesselInfo')
                .map((layer) => {
                    return new GeoJsonLayer<GeoJSONFeature>({
                        id: layer.uuid,
                        //@ts-ignore
                        name: layer.name,
                        //@ts-ignore
                        data: features
                            .filter((x: GeoJSONFeature) => {
                                return x.properties?.layer === layer.name;
                            })
                            .filter((f): f is GeoJSONFeature => f !== null),
                        index: layer.drawIndex,
                        pickable: true,
                        stroked: true, // Enable stroked borders for the polygons
                        filled: true, // Enable filling for the polygons
                        extruded: false,
                        pointRadiusScale: 10,
                        lineWidthUnits: 'pixels',
                        getFillColor: (feature): RGBAColor => {
                            return feature.properties?.visible
                                ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                                : [0, 0, 0, 0]; // Transparent if not visible
                        },
                        getLineColor: (feature): RGBAColor => {
                            return feature.properties?.visible
                                ? [
                                      ...hexToRgb(feature.properties?.stroke.colour),
                                      feature.properties?.stroke.opacity * 255,
                                  ]
                                : [0, 0, 0, 0]; // Transparent if not visible
                        },
                        getLineWidth: (feature) =>
                            feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0, // 0 width hides the stroke

                        getLineOpacity: (feature) => {
                            return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1; // Default opacity: 1 if not provided
                        },
                    }) as unknown as DeckGLLayer;
                })
        );

        await this.renderMap({ layers: layers });
    };

    render() {
        return (
            <div
                className={twclsx(
                    !this.state.active && 'hidden',
                    'mt-14',
                    'h-page',
                    'padding-transition',
                    'fixed',
                    'z-10',
                    'left-0',
                    'right-0',
                    'margin-transition',
                    'ml-14',
                    'w-fullPage'
                )}
            >
                {this.props.layout.splashScreen && (
                    <div className=" w-full h-full pt-40 pl-20  items-center text-center  ">
                        <Image
                            alt={translate('transit analysis')}
                            className="w-full select-none h-full object-cover absolute left-0 top-0 z-20"
                            src="/static/images/hero.jpg"
                        ></Image>
                        <div className="w-full items-start	flex-col	flex-nowrap  h-full flex z-30">
                            <Image
                                alt={translate('transit analysis')}
                                className="w-splashlogo -mt-10 select-none  z-30 top-0  mx-auto   "
                                src="/static/images/logo.svg"
                            ></Image>

                            <div className=" select-none whitespace-nowrap  text-4xl font-bold z-30 top-0  mx-auto   ">
                                {this.props.port.client_name}
                            </div>
                        </div>
                    </div>
                )}
                <canvas
                    id={this.state.uuid}
                    ref={this.canvasRef}
                    className={twclsx(
                        'fixed',
                        this.state.drawingMode !== 'pointer' && 'mouse-crosshair',
                        'z-10',
                        'w-screen',
                        'h-screen',
                        'left-0',
                        'right-0'
                    )}
                ></canvas>
                <ToolTip
                    mode="search"
                    enviromental={this.state.switchs?.chartEnviromentalDataOnRightColumn?.value}
                    manoeuvre={this.state.switchs?.chartManoeuvreInformationOnRightColumn?.value}
                    updateTooltips={this.updateTooltips}
                    updateDrawinglabel={this.updateDrawinglabel}
                    tooltips={this.state.tooltips}
                ></ToolTip>

                <ChangeLayer
                    save={true}
                    backgrounds={this.state.backgrounds}
                    changeBackground={this.changeBackground}
                    changeOverlay={this.changeOverlay}
                    move={true}
                ></ChangeLayer>
                <MeasurePosition
                    hoverMeasureData={this.state.hoverMeasureData}
                    coordinate={this.state?.coordinate}
                ></MeasurePosition>
                <MousePosition backgrounds={this.state.backgrounds} coordinate={this.state?.coordinate}></MousePosition>
                <Attribution backgrounds={this.state.backgrounds}></Attribution>
                <Controls parent="chart" move={true}></Controls>
            </div>
        );
    }
}

const chartStateToProps = (state: RootState) => ({
    layout: state.layout.layout,
    vesselCategories: state.port.vesselCategories,
    port: state.port.port,
    layers: state.layout.layers,
    outlineInterval: state.map.outlineInterval,
    switchs: state.layout.switchs,
    pilotagePlanSettings: state.layout.pilotagePlanSettings,
    pilotagePlans: state.port.pilotagePlans,
    mainControls: state.layout.mainControls,
    menuControls: state.layout.menuControls,
    dateTimeFormat: state.port.dateTimeFormat,
    openModelFunction: state.layout.openModelFunction,
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const chartDispatchToProps = (dispatch: Dispatch) => ({
    setMapAction: bindActionCreators(setMapAction, dispatch),
    setMapControlsAction: bindActionCreators(setMapControlsAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    setSystemLoadingAction: bindActionCreators(setSystemLoadingAction, dispatch),
    showMessageAction: bindActionCreators(showMessageAction, dispatch),
});

export default connect(chartStateToProps, chartDispatchToProps)(Chart);
